import { ChevronDown, Circle, Mic, MicOff, ScrollText, ShieldCheck, Volume2, Webcam } from 'lucide-react';
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { availableModes, NOTES_SPEEDS, usesCamera, usesScreen, type NotesSpeed, type RecordingMode } from '@/core/recording/session';
import { formatBytes } from '@/core/time/format';
import { cn } from '@/lib/cn';
import { summarize } from '@/lib/network-ledger';
import { captureSupport, type DeviceChoice } from '@/lib/recording/capture';
import { useLedgerStore } from '@/state/ledger-store';
import { microphoneLevel, useRecordingStore } from '@/state/recording-store';
import { Button, Notice, Segmented, Spinner, SwitchButton } from '../primitives';
import { deviceProblemKey, LevelMeter, LiveVideo } from './media';

/** A native select dressed as a quiet text control; just a label while the browser keeps device names hidden. */
function DeviceSelect({ devices, value, fallback, label, onChange }: { readonly devices: readonly DeviceChoice[]; readonly value: string | null; readonly fallback: string; readonly label: string; readonly onChange: (id: string) => void }) {
  const { t } = useTranslation();
  if (devices.length <= 1) return <span className="min-w-0 flex-1 truncate text-sm text-fg">{devices[0]?.label && devices[0].label.length > 1 ? devices[0].label : fallback}</span>;
  return (
    <span className="relative flex min-w-0 flex-1 items-center">
      <select
        aria-label={label}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        className="w-full min-w-0 cursor-pointer appearance-none truncate rounded-md bg-transparent py-1 pr-5 text-sm text-fg outline-offset-2"
      >
        {!value && <option value="">{t('record.defaultDevice')}</option>}
        {devices.map((device) => (
          <option key={device.id} value={device.id}>
            {device.label}
          </option>
        ))}
      </select>
      <ChevronDown size={14} className="pointer-events-none absolute right-0 text-fg-subtle" />
    </span>
  );
}

function SourceRow({ icon, children, off }: { readonly icon: ReactNode; readonly children: ReactNode; readonly off?: boolean }) {
  return (
    <div className="flex min-h-12 items-center gap-3 px-3.5 py-2">
      <span className={cn('shrink-0', off ? 'text-fg-subtle' : 'text-fg-muted')}>{icon}</span>
      {children}
    </div>
  );
}

/**
 * Speaker notes, folded into one row until opened: read them in the floating
 * window while recording, scrolling at the chosen pace.
 */
function NotesRow() {
  const { t } = useTranslation();
  const notes = useRecordingStore((state) => state.notes);
  const speed = useRecordingStore((state) => state.notesSpeed);
  const mode = useRecordingStore((state) => state.mode);
  const { setNotes, setNotesSpeed } = useRecordingStore.getState();
  const [open, setOpen] = useState(false);
  const panel = useId();
  const lines = notes.split('\n').filter((line) => line.trim()).length;
  return (
    <div>
      <SourceRow icon={<ScrollText size={16} />} off={!notes.trim()}>
        <button type="button" aria-expanded={open} aria-controls={panel} onClick={() => setOpen(!open)} className="flex min-w-0 flex-1 items-center gap-3 rounded-md text-left">
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm text-fg">{t('record.notes')}</span>
            <span className="block truncate text-xs text-fg-subtle">{lines > 0 ? t('record.notesLines', { count: lines }) : t('record.notesHint')}</span>
          </span>
          <ChevronDown size={16} className={cn('shrink-0 text-fg-subtle transition-transform duration-200 ease-out-soft', open && 'rotate-180')} />
        </button>
      </SourceRow>
      {open && (
        <div id={panel} className="space-y-2.5 px-3.5 pb-3.5">
          <textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            rows={6}
            aria-label={t('record.notes')}
            placeholder={t('record.notesPlaceholder')}
            className="scrollbar-thin block w-full resize-y rounded-xl bg-panel px-3 py-2.5 text-sm leading-relaxed text-fg ring-1 ring-line ring-inset placeholder:text-fg-subtle"
          />
          <Segmented<NotesSpeed>
            size="sm"
            label={t('record.notesSpeed')}
            value={speed}
            onChange={setNotesSpeed}
            options={NOTES_SPEEDS.map((value) => ({ value, label: t(`record.notesSpeeds.${value}`) }))}
          />
          {notes.trim() && usesScreen(mode) && <p className="text-xs leading-snug text-pretty text-fg-subtle">{t('record.notesShareHint')}</p>}
        </div>
      )}
    </div>
  );
}

/**
 * The recording band's setup, in the spot where other bands take a file: what to
 * record, which microphone and camera, then one button. Previews start when the
 * band opens and stop when it closes, so the camera light is never on for nothing.
 */
export function RecordSetup({ loading }: { readonly loading: string | false }) {
  const { t, i18n } = useTranslation();
  const store = useRecordingStore();
  const { mode, options, devices, deviceProblems, micSilent, storage, phase } = store;
  const modes = useMemo(() => availableModes(captureSupport()), []);
  const uploaded = summarize(useLedgerStore((state) => state.entries)).uploadedBytes;
  const systemId = useId();
  const choosing = phase.status === 'choosing';

  useEffect(() => {
    void useRecordingStore.getState().activate();
    void useRecordingStore.getState().refreshLeftovers();
    return () => useRecordingStore.getState().deactivate();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-30 items-center justify-center gap-2.5 rounded-2xl bg-elevated text-sm font-medium text-fg-muted ring-1 ring-line ring-inset">
        <Spinner className="text-accent-text" />
        {loading}
      </div>
    );
  }

  const micProblem = deviceProblems.microphone;
  const cameraProblem = usesCamera(mode) ? deviceProblems.camera : undefined;

  return (
    <div className="flex flex-col gap-3">
      {modes.length > 1 && (
        <Segmented<RecordingMode>
          label={t('record.modesLabel')}
          size="sm"
          value={mode}
          onChange={(next) => void store.setMode(next)}
          options={modes.map((value) => ({ value, label: t(`record.modes.${value}`) }))}
        />
      )}

      <div role="group" aria-label={t('record.sources')} className="divide-y divide-line rounded-2xl bg-elevated ring-1 ring-line ring-inset">
        <SourceRow icon={options.microphone ? <Mic size={16} /> : <MicOff size={16} />} off={!options.microphone}>
          <DeviceSelect
            devices={devices.microphones}
            value={options.microphoneId}
            fallback={t('record.microphone')}
            label={t('record.chooseMicrophone')}
            onChange={(id) => void store.setMicrophoneDevice(id)}
          />
          {options.microphone && !micProblem && <LevelMeter level={microphoneLevel} label={t('record.micLevel')} className="text-accent-text" />}
          <SwitchButton checked={options.microphone} onChange={(on) => void store.setMicrophone(on)} label={t('record.microphone')} />
        </SourceRow>
        {usesCamera(mode) && (
          <SourceRow icon={<Webcam size={16} />}>
            <DeviceSelect devices={devices.cameras} value={options.cameraId} fallback={t('record.camera')} label={t('record.chooseCamera')} onChange={(id) => void store.setCameraDevice(id)} />
          </SourceRow>
        )}
        {usesScreen(mode) && (
          <SourceRow icon={<Volume2 size={16} />} off={!options.systemAudio}>
            <label htmlFor={systemId} className="min-w-0 flex-1 cursor-pointer select-none">
              <span className="block truncate text-sm text-fg">{t('record.systemAudio')}</span>
              {options.systemAudio && <span className="block text-xs leading-snug text-pretty text-fg-subtle">{t('record.systemAudioHint')}</span>}
            </label>
            <SwitchButton id={systemId} checked={options.systemAudio} onChange={store.setSystemAudio} />
          </SourceRow>
        )}
        <NotesRow />
      </div>

      {micProblem && <Notice tone="danger">{t(`record.microphoneProblem.${deviceProblemKey(micProblem)}`)}</Notice>}
      {cameraProblem && <Notice tone="danger">{t(`record.cameraProblem.${deviceProblemKey(cameraProblem)}`)}</Notice>}
      {micSilent && options.microphone && !micProblem && <Notice icon={<MicOff size={15} />}>{t('record.micSilent')}</Notice>}
      {storage.verdict === 'low' && storage.minutes !== null && <Notice>{t('record.storageLow', { minutes: storage.minutes })}</Notice>}
      {!modes.includes('screen') && <Notice>{t('record.screenUnavailable')}</Notice>}

      <Button
        variant="primary"
        size="lg"
        className="w-full"
        disabled={choosing || modes.length === 0 || (mode === 'camera' && cameraProblem !== undefined)}
        icon={choosing ? <Spinner /> : <Circle size={12} className="fill-current" />}
        onClick={() => void store.start()}
      >
        {choosing ? t('record.choosing') : t('record.start')}
      </Button>
      <p className="flex items-center justify-center gap-1.5 text-[13px] leading-snug text-fg-muted tabular">
        <ShieldCheck size={14} className="shrink-0 text-accent-text" />
        {t('record.trust', { size: formatBytes(uploaded, i18n.language) })}
      </p>
    </div>
  );
}

/** The recording band's result column: the person's own camera, live, the way the bubble will show it. */
export function RecordPreview({ fallback }: { readonly fallback: ReactNode }) {
  const { t } = useTranslation();
  const stream = useRecordingStore((state) => state.cameraStream);
  const mode = useRecordingStore((state) => state.mode);
  if (!stream || !usesCamera(mode)) return <>{fallback}</>;
  return mode === 'camera' ? (
    <LiveVideo stream={stream} mirrored label={t('record.cameraPreview')} className="h-24 shrink-0 rounded-xl bg-stage ring-1 ring-line aspect-video" />
  ) : (
    <LiveVideo stream={stream} mirrored label={t('record.cameraPreview')} className="size-24 shrink-0 rounded-full bg-stage shadow-float ring-1 ring-line" />
  );
}
